import React, { Suspense } from 'react';
import { useEntityCustomisations } from '../queries/useEntityCustomisations';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { homepageConstants, ROUTES_PATH } from '../constants/globalConstant';
import Teams from '../views/teammanagement/mobile';
import WebTeams from '../views/teammanagement/web';
const HomepagesView = React.lazy(() => import('../views/HomepagesView/Listing'));
const MenuBuilderView = React.lazy(() => import('../views/MenuBuilder/Listing'));
const HomeScreen = React.lazy(() => import('../views/HomeScreen'));
const MenuBuilder = React.lazy(() => import('../views/MenuBuilder/Builder'));
const Templates = React.lazy(() => import('../views/Templates'));
import { sideMenuOptions, sideMenuBottomOptions } from '../utils/homepageUtils';
const LeadControlsList = React.lazy(() => import('../views/LeadControls/Listing'));
const ThemeBuilderMain = React.lazy(() => import('../views/ThemeBuilder/main.js'));
const LeadControls = React.lazy(() => import('../views/LeadControls/Builder'));
const CasaWebHomepageBuilder = React.lazy(() => import('../views/CasaWebHomepage/Builder2'));
const CasaWebHomepageListing = React.lazy(() => import('../views/CasaWebHomepage/Listing'));
const LaunchBar = React.lazy(() => import('../views/LaunchBar/Listing'));
const LaunchbarHomepage = React.lazy(() => import('../views/LaunchBar/Builder'));
const TopNavigation = React.lazy(() => import('../views/TopNavigation/Listing'));
const TopNavigationBuilder = React.lazy(() => import('../views/TopNavigation/Builder'));
const Object360View = React.lazy(() => import('../views/Object360/Listing'));

const SideNavigationMenu = React.lazy(() => import('../components/SideNavigationMenu'));
const Object360Builder = React.lazy(() => import('../views/Object360/Builder'));
const Announcements = React.lazy(() => import('../views/Announcements/Listing/index.js'));
const AnnouncementBuilderMain = React.lazy(
  () => import('../views/Announcements/Builder/Builder.js')
);

import { getDynamicNavigationLabels } from '../utils/sideNavUtils.js';
import { FeatureProvider } from '../hooks/FeatureContext.js';
import { SidePanelProvider } from '../contexts/SidePanelContext.js';
import { useEmbedMode } from '../hooks/useEmbedMode.js';
import { useDashboardConfig } from '../queries/useDashboardConfig.js';
import { useLeadTypes } from '../hooks/useLeadTypes.js';
import { getBasename } from '../utils/publicPath';

import '../styles/taskTypesMultiselectDropdown.css';

function AppRoutes() {
  const { data: appConfig } = useDashboardConfig();
  const { data: entityCustomisation } = useEntityCustomisations();
  const { leadTypesEnabled } = useLeadTypes();
  const isEmbed = useEmbedMode();

  const featureNavigationConfig = {
    [homepageConstants.TEAMS]: appConfig?.mobileHomeBuilderEnabled || appConfig?.casaWebEnabled,
    [homepageConstants.MOBILE]: appConfig?.mobileHomeBuilderEnabled,
    [homepageConstants.WEB]: appConfig?.casaWebEnabled
  };

  let defaultPath = ROUTES_PATH.home;
  let defaultPathComponent = <Teams />;

  if (!appConfig?.mobileHomeBuilderEnabled && appConfig?.casaWebEnabled) {
    defaultPath = ROUTES_PATH.webTeams;
    defaultPathComponent = <WebTeams />;
  }

  const dynamicLabels = getDynamicNavigationLabels(entityCustomisation, leadTypesEnabled);

  const sideNavigationOptions = sideMenuOptions(defaultPath, dynamicLabels).map((option) => {
    option.isEnabled = featureNavigationConfig[option.groupName];
    return option;
  });

  return (
    <Router data-testid="app-routes" basename={getBasename(process.env.PUBLIC_URL)}>
      <SidePanelProvider>
        <Suspense
          fallback={
            <div id="initial-casa-loader">
              <div></div>
            </div>
          }>
          {!isEmbed && (
            <SideNavigationMenu
              sideMenuOptions={sideNavigationOptions}
              sideMenuBottomOptions={sideMenuBottomOptions}
            />
          )}
          <div className={` overflow-hidden main-content${isEmbed ? ' main-content--embed' : ''}`}>
            <FeatureProvider>
              <Routes>
                {featureNavigationConfig[homepageConstants.TEAMS] && (
                  <Route path={''} element={defaultPathComponent} />
                )}
                {featureNavigationConfig[homepageConstants.MOBILE] && (
                  <>
                    <Route path={ROUTES_PATH.homepages} element={<HomepagesView />} />
                    <Route path={ROUTES_PATH.menus} element={<MenuBuilderView />} />
                    <Route
                      path={`${ROUTES_PATH.homebuilder}/:homepageId?`}
                      element={<HomeScreen />}
                    />
                    <Route path={`${ROUTES_PATH.menubuilder}/:menuId?`} element={<MenuBuilder />} />
                    <Route path={ROUTES_PATH.templates} element={<Templates />} />
                    <Route path={ROUTES_PATH.themebuilder} element={<ThemeBuilderMain />} />
                    <Route path={ROUTES_PATH.leadControls} element={<LeadControlsList />} />
                    <Route
                      path={`${ROUTES_PATH.leadControlBuilder}/:leadControlId?`}
                      element={<LeadControls />}
                    />
                  </>
                )}
                {featureNavigationConfig[homepageConstants.WEB] && (
                  <>
                    <Route path={ROUTES_PATH.webDashboards} element={<CasaWebHomepageListing />} />
                    <Route path={ROUTES_PATH.webTeams} element={<WebTeams />} />
                    <Route
                      path={`${ROUTES_PATH.webHomepageBuilder}/:dashboardId?`}
                      element={<CasaWebHomepageBuilder />}
                    />
                    <Route path={ROUTES_PATH.launchBar} element={<LaunchBar />} />
                    <Route
                      path={`${ROUTES_PATH.launchBarHomePage}/:launchbarId?`}
                      element={<LaunchbarHomepage />}
                    />
                    <Route
                      path={`${ROUTES_PATH.topNavigationBuilder}/:topNavId?`}
                      element={<TopNavigationBuilder />}
                    />
                    <Route path={ROUTES_PATH.object360} element={<Object360View />} />
                    <Route
                      path={`${ROUTES_PATH.object360Builder}/:object360Id?`}
                      element={<Object360Builder />}
                    />
                  </>
                )}
                <Route path="*" element={<Navigate to={{ pathname: defaultPath }} />} />
                <Route path={ROUTES_PATH.topNavigation} element={<TopNavigation />} />
                <Route path={ROUTES_PATH.announcements} element={<Announcements />} />
                <Route
                  path={`${ROUTES_PATH.announcementBuilder}/:announcementId?`}
                  element={<AnnouncementBuilderMain />}
                />
              </Routes>
            </FeatureProvider>
          </div>
        </Suspense>
      </SidePanelProvider>
    </Router>
  );
}

export default AppRoutes;
